import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Users, User } from 'lucide-react'
import type { DepartmentSummary } from '@/types/commission'

interface DepartmentSummaryTableProps {
  departmentSummary: DepartmentSummary[]
  isLoading?: boolean
}

export function DepartmentSummaryTable({
  departmentSummary,
  isLoading,
}: DepartmentSummaryTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card">
        <div className="px-6 py-4 border-b">
          <Skeleton className="h-5 w-32" />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><Skeleton className="h-4 w-24" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (departmentSummary.length === 0) {
    return (
      <div className="rounded-xl border bg-card">
        <div className="px-6 py-4 border-b">
          <h3 className="text-sm font-medium">部门佣金汇总</h3>
        </div>
        <div className="py-12 text-center">
          <p className="text-muted-foreground">暂无部门汇总数据</p>
        </div>
      </div>
    )
  }

  // 计算总计
  const totalAmount = departmentSummary.reduce((sum, dept) => sum + dept.total_amount, 0)
  const totalCount = departmentSummary.reduce((sum, dept) => sum + dept.total_count, 0)
  const totalUsers = departmentSummary.reduce((sum, dept) => sum + dept.user_count, 0)

  return (
    <div className="rounded-xl border bg-card">
      <div className="px-6 py-4 border-b">
        <h3 className="text-sm font-medium">部门佣金汇总</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>部门名称</TableHead>
            <TableHead className="text-right">佣金总额</TableHead>
            <TableHead className="text-right">佣金笔数</TableHead>
            <TableHead className="text-right">员工人数</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {departmentSummary.map((dept) => (
            <TableRow key={dept.department_id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{dept.department_name}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold">¥{dept.total_amount.toFixed(2)}</span>
              </TableCell>
              <TableCell className="text-right">
                <span className="text-sm">{dept.total_count} 笔</span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm">{dept.user_count} 人</span>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {/* 总计行 */}
          <TableRow className="bg-muted/50 font-medium">
            <TableCell>总计</TableCell>
            <TableCell className="text-right">
              <span className="font-bold text-primary">¥{totalAmount.toFixed(2)}</span>
            </TableCell>
            <TableCell className="text-right">{totalCount} 笔</TableCell>
            <TableCell className="text-right">{totalUsers} 人</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
